import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { Button } from '@/src/components/ui/button'
import { listPrompts, reorderPrompts, upsertPrompt } from '@/src/api/prompt'
import { DotsSixVerticalIcon } from '@phosphor-icons/react'
import { EditAction } from '@/src/components/manage/actions/EditAction'
import { EnabledAction } from '@/src/components/manage/actions/EnabledAction'
import { useTranslation } from '@/src/i18n/client'
import type { Prompt } from '@/src/types/prompt.zod'
import { SimpleCopyCell } from '@/src/components/manage/tables/CopyableCell'
import { toast } from 'sonner'
import { useState, useEffect, ReactNode } from 'react'
import { DataTable, ColumnConfig } from '@/src/components/manage/tables/DataTable'
import { TableCell } from '@/src/components/ui/table'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

export const Route = createFileRoute('/_manage/manage/admin/tasks/')({
  component: AdminTasksPage,
})

function formatCellValue(task: Prompt, key: string): ReactNode {
  const value = (task as Record<string, unknown>)[key]
  if (value === null || value === undefined) return '-'
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  return '-'
}

function SortableRow({ task, columns, actions }: { task: Prompt; columns: ColumnConfig<Prompt>[]; actions?: (task: Prompt) => ReactNode }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id! })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <tr ref={setNodeRef} style={style} className="hover:bg-muted/50 data-[state=selected]:bg-muted border-b transition-colors">
      {columns.map((col) => (
        <TableCell
          key={col.key}
          className={col.align === "right" ? "text-right" : undefined}
        >
          {col.key === 'drag' ? (
            <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
              {col.render ? col.render(task) : formatCellValue(task, col.key)}
            </div>
          ) : (
            <>{col.render ? col.render(task) : formatCellValue(task, col.key)}</>
          )}
        </TableCell>
      ))}
      {actions && (
        <TableCell className="text-right pr-0">
          {actions(task)}
        </TableCell>
      )}
    </tr>
  )
}

function AdminTasksPage() {
  const { t } = useTranslation('manage')
  const queryClient = useQueryClient()
  const [sortedTasks, setSortedTasks] = useState<Prompt[]>([])

  const { data: tasks, isLoading, error } = useQuery({
    queryKey: ['prompt', 'list', 'all'],
    queryFn: () => listPrompts({ data: { all: true } }),
  })

  // Update sorted tasks when data changes
  useEffect(() => {
    setSortedTasks(tasks ? [...tasks] : [])
  }, [tasks])

  const reorderMutation = useMutation({
    mutationFn: (ids: string[]) => reorderPrompts({ data: { ids } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompt'] })
    },
    onError: (err: any) => {
      toast.error(err?.message || t('tasks.failedToReorderTasks'))
      // Revert on error
      setSortedTasks(tasks ? [...tasks] : [])
    },
  })

  const toggleEnabledMutation = useMutation({
    mutationFn: ({ task, enabled }: { task: Prompt; enabled: boolean }) =>
      upsertPrompt({
        data: {
          id: task.id,
          name: task.name,
          description: task.description,
          prompt: task.prompt ?? '',
          enabled,
          order: (task as any).order,
          defaultAiModelId: task.defaultAiModelId,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompt'] })
    },
    onError: (err: any) => {
      toast.error(err?.message || t('tasks.failedToUpdateTask'))
    },
  })

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      setSortedTasks((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id)
        const newIndex = items.findIndex((item) => item.id === over.id)
        const newOrder = arrayMove(items, oldIndex, newIndex)
        
        // Update order on server
        const ids = newOrder.map((item) => item.id!)
        reorderMutation.mutate(ids)
        
        return newOrder
      })
    }
  }

  const columns: ColumnConfig<Prompt>[] = [
    {
      key: 'drag',
      label: '',
      width: 'w-12',
      render: () => (
        <DotsSixVerticalIcon className="w-4 h-4 text-muted-foreground" />
      ),
    },
    {
      key: 'id',
      label: t('table.id'),
      width: 'w-24',
      render: (task) => task.id ? <SimpleCopyCell value={task.id} truncateLength={20} /> : '-',
    },
    {
      key: 'name',
      label: t('table.name'),
    },
    {
      key: 'description',
      label: t('table.description'),
      render: (task) => task.description || '-',
    },
    {
      key: 'enabled',
      label: t('table.status'),
      align: 'right',
      render: (task) => (
        <EnabledAction
          enabled={!!task.enabled}
          onToggle={async (enabled) => {
            await toggleEnabledMutation.mutateAsync({ task, enabled })
          }}
        />
      ),
    },
  ];

  const renderActions = (task: Prompt) => task.id ? (
    <EditAction
      to="/manage/admin/tasks/$id"
      params={{ id: task.id }}
    />
  ) : null

  return (
    <div className="bg-background text-foreground">
      <AdminToolbar>
        <AdminToolbar.Left>
          <Link to="/manage/admin/tasks/$id" params={{ id: 'new' }}>
            <Button size="lg">Create Task</Button>
          </Link>
        </AdminToolbar.Left>
      </AdminToolbar>
      <div className="mt-4">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={(sortedTasks.length > 0 ? sortedTasks : (tasks || [])).map((t: Prompt) => t.id!)}
            strategy={verticalListSortingStrategy}
          >
            <DataTable<Prompt>
              data={sortedTasks.length > 0 ? sortedTasks : (tasks || [])}
              columns={columns}
              isLoading={isLoading}
              error={error instanceof Error ? error : error ? String(error) : null}
              emptyMessage={t('table.noTasks')}
              actions={renderActions}
              renderRow={(task) => (
                <SortableRow
                  key={task.id}
                  task={task}
                  columns={columns}
                  actions={renderActions}
                />
              )}
            />
          </SortableContext>
        </DndContext>
      </div>
    </div>
  )
}
